import {
  ActionError,
  Button,
  ChoiceRow,
  FieldGrid,
  FormBlock,
  FormFooter,
  PageHeader,
  PageTitle,
  Radio,
  RadioGroup,
  ReadingColumn,
  titleCase,
  toast,
} from '@westhetics/ui'
import { useRef, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router'
import { Page } from '@/app/shell'
import { TextField } from '@/features/clinic-profile/blocks'
import { emailError, personNameError, placeFieldErrors } from '@/features/clinic-profile/form'
import {
  focusFirstInvalid,
  useFormState,
  type FormErrors,
} from '@/features/clinic-profile/use-form-state'
import { isApiError } from '@/lib/api'
import { errorMessage } from '@/lib/errors'
import { routes } from '@/routes'
import { useCreateUser, type AccessMode, type CreatedUser, type MemberRole } from './api'
import { ROLES, ROLE_LABELS } from './labels'
import { RoleFields } from './role-fields'
import { TemporaryPassword } from './temporary-password'

/**
 * Criar usuário (docs/areas/administracao.md, fluxo "Criar usuário"; RN-ADM-10 e 12). Arquétipo:
 * cadastro. O perfil não tem padrão: é permissão, e quem cria escolhe. O convite por e-mail é o
 * modo de acesso padrão; a senha temporária aparece uma única vez, nesta tela.
 */

interface NewUserFormState {
  name: string
  email: string
  role: MemberRole | ''
  practitionerId: string
  accessMode: AccessMode
}

const EMPTY: NewUserFormState = {
  name: '',
  email: '',
  role: '',
  practitionerId: '',
  accessMode: 'invite',
}

const KEYS = ['name', 'email', 'role', 'practitionerId'] as const

// As mensagens são as da API, para o erro dizer o mesmo dos dois lados.
function validate(values: NewUserFormState): FormErrors<NewUserFormState> {
  return {
    name: personNameError(values.name),
    email: emailError(values.email),
    role: values.role === '' ? 'Escolha o perfil: gestora, recepção ou prestador.' : undefined,
    practitionerId:
      values.role === 'practitioner' && values.practitionerId === ''
        ? 'Escolha qual prestadora esta pessoa é.'
        : undefined,
  }
}

const FIELD_KEYS: Record<string, keyof NewUserFormState> = {
  name: 'name',
  email: 'email',
  role: 'role',
  practitionerId: 'practitionerId',
  accessMode: 'accessMode',
}

const fieldKey = (path: string) => FIELD_KEYS[path]

export function NewUserPage() {
  const navigate = useNavigate()
  const create = useCreateUser()
  const form = useFormState<NewUserFormState>(() => EMPTY, validate)
  const formRef = useRef<HTMLFormElement>(null)
  // Com senha temporária, a tela fica para mostrar a senha uma única vez (RN-ADM-10).
  const [created, setCreated] = useState<CreatedUser | null>(null)

  function submit(event: FormEvent) {
    event.preventDefault()
    const values = form.values
    if (!form.check(KEYS) || values.role === '') {
      focusFirstInvalid(formRef.current)
      return
    }
    create.mutate(
      {
        name: values.name.trim(),
        email: values.email.trim().toLowerCase(),
        role: values.role,
        ...(values.role === 'practitioner' ? { practitionerId: values.practitionerId } : {}),
        accessMode: values.accessMode,
      },
      {
        onSuccess: (result) => {
          const { access, user } = result
          const name = titleCase(user.name)
          if (access.mode === 'temporary_password') {
            setCreated(result)
            return
          }
          if (access.mode === 'existing_membership') {
            toast(
              access.emailSent
                ? `${name} já tinha conta; agora tem acesso a esta clínica`
                : `${name} já tinha conta; agora tem acesso a esta clínica — o e-mail de aviso não saiu`,
            )
            navigate(routes.admin.users)
            return
          }
          if (access.emailSent) {
            toast(`Convite enviado a ${user.email} — vale 7 dias`)
            navigate(routes.admin.users)
            return
          }
          // O usuário existe, mas o convite não chegou: a saída é reenviar, na edição dele.
          toast('O convite foi criado, mas o e-mail não saiu. Reenvie o convite.')
          navigate(routes.admin.user(user.id))
        },
        onError: (error) => {
          form.refuse(placeFieldErrors<NewUserFormState>(error, fieldKey).fields)
          focusFirstInvalid(formRef.current)
        },
      },
    )
  }

  if (created) return <CreatedWithTemporaryPassword created={created} />

  const failure = create.isError ? placeFieldErrors<NewUserFormState>(create.error, fieldKey) : null
  const showFailure =
    failure !== null && (Object.keys(failure.fields).length === 0 || failure.unplaced.length > 0)
  const existing =
    create.error && isApiError(create.error) && create.error.code === 'conflict'
      ? (create.error.details.existing as { id: string } | undefined)
      : undefined

  return (
    <form ref={formRef} onSubmit={submit} noValidate>
      <PageHeader
        LinkComponent={Link}
        crumbs={[{ label: 'Usuários', href: routes.admin.users }, { label: 'novo' }]}
      />
      <Page>
        <ReadingColumn wide>
          <PageTitle
            title="Novo usuário"
            subtitle="Usuário é quem entra no sistema. Quem só responde tecnicamente pelo procedimento, sem entrar, é cadastrado em Prestadores."
          />

          <div className="mt-6 flex flex-col gap-4">
            {showFailure ? (
              <ActionError
                title="Não foi possível criar o usuário"
                description={
                  failure.unplaced.length > 0
                    ? failure.unplaced.join(' ')
                    : `${errorMessage(create.error)} Nada foi criado; o que você digitou continua na tela.`
                }
                actions={
                  existing ? (
                    <Button variant="outline" size="sm" asChild>
                      <Link to={routes.admin.user(existing.id)}>Abrir o usuário existente</Link>
                    </Button>
                  ) : undefined
                }
              />
            ) : null}

            <FormBlock
              title="Identificação"
              note="O nome aparece na trilha de ações e nos documentos que a pessoa assinar pela clínica. O e-mail é o login e não muda depois."
            >
              <FieldGrid>
                <TextField form={form} name="name" label="Nome" maxLength={120} />
                <TextField
                  form={form}
                  name="email"
                  label="E-mail"
                  type="email"
                  inputMode="email"
                  autoCapitalize="none"
                  placeholder="nome@clinica.com.br"
                />
              </FieldGrid>
            </FormBlock>

            <FormBlock
              title="Permissão"
              note="Define o que a pessoa vê e faz nesta clínica. Depois de criado, o papel só muda por Trocar papel, com justificativa."
            >
              <RoleFields
                label="Permissão"
                hideLabel
                roles={ROLES}
                role={form.values.role === '' ? null : form.values.role}
                practitionerId={form.values.practitionerId}
                onRole={(role) => form.update({ role, practitionerId: '' })}
                onPractitioner={(practitionerId) => form.update({ practitionerId })}
                onPractitionerBlur={() => form.touch('practitionerId')}
                errors={{ role: form.errors.role, practitionerId: form.errors.practitionerId }}
              />
            </FormBlock>

            <FormBlock
              title="Acesso"
              note="Como a pessoa entra pela primeira vez. Se o e-mail já tem conta na Westhetics, ela entra com a senha que já tem."
            >
              <RadioGroup
                aria-label="Acesso"
                className="tablet:grid-cols-2"
                value={form.values.accessMode}
                onValueChange={(value) => form.update({ accessMode: value as AccessMode })}
              >
                <ChoiceRow
                  control={<Radio value="invite" />}
                  description="A pessoa recebe um link para definir a senha, válido por 7 dias."
                >
                  Convite por e-mail
                </ChoiceRow>
                <ChoiceRow
                  control={<Radio value="temporary_password" />}
                  description="A senha aparece uma única vez nesta tela, para você entregar. A troca é obrigatória no primeiro acesso."
                >
                  Senha temporária
                </ChoiceRow>
              </RadioGroup>
              {form.errors.accessMode ? (
                <p
                  role="alert"
                  className="font-body text-eyebrow text-plum mt-[6px] leading-[1.5] font-light tracking-normal"
                >
                  {form.errors.accessMode}
                </p>
              ) : null}
            </FormBlock>
            <FormFooter
              secondary={
                <Button variant="outline" asChild>
                  <Link to={routes.admin.users}>Cancelar</Link>
                </Button>
              }
              primary={
                <Button type="submit" loading={create.isPending} loadingLabel="Criando o usuário…">
                  Criar usuário
                </Button>
              }
            />
          </div>
        </ReadingColumn>
      </Page>
    </form>
  )
}

/** O usuário foi criado com senha temporária: a senha aparece aqui, uma única vez, com "Copiar". */
function CreatedWithTemporaryPassword({ created }: { created: CreatedUser }) {
  const { access, user } = created
  const password = access.mode === 'temporary_password' ? access.temporaryPassword : null
  const name = titleCase(user.name)

  return (
    <>
      <PageHeader
        LinkComponent={Link}
        crumbs={[{ label: 'Usuários', href: routes.admin.users }, { label: name }]}
      />
      <Page>
        <ReadingColumn>
          <PageTitle
            title="Usuário criado"
            subtitle={`${name} entra nesta clínica como ${ROLE_LABELS[user.role].toLowerCase()}. Falta entregar a senha.`}
          />
          <TemporaryPassword
            className="mt-6"
            name={user.name}
            password={password}
            missing={
              <>
                O usuário já tinha sido criado na primeira tentativa, e a senha só é mostrada uma
                vez. Para {user.email} entrar, abra o usuário e resete a senha.
              </>
            }
          >
            Esta senha não aparece de novo. Copie e entregue a {user.email}; a troca é obrigatória
            no primeiro acesso.
          </TemporaryPassword>
          {!access.emailSent ? (
            <p className="font-body text-secondary mt-3 text-[12px] leading-[1.6] font-light">
              O e-mail avisando {user.email} do acesso não saiu. Entregue a senha por outro canal.
            </p>
          ) : null}
          <div className="mt-6 flex flex-wrap gap-[10px]">
            <Button asChild>
              <Link to={routes.admin.users}>Voltar para Usuários</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to={routes.admin.user(user.id)}>Abrir o usuário</Link>
            </Button>
          </div>
        </ReadingColumn>
      </Page>
    </>
  )
}
